import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import type { IImage } from 'tbk-components/src/components/Image';

export interface IMediaFile extends IImage {
	size?: number;
	prettySize?: string;
}

export interface IInvestorKitItems {
	/**
	 * Item Label
	 */
	label?: string;
	/**
	 * Item Link
	 *
	 * @control mediafile
	 */
	link?: IMediaFile;
	/**
	 * Item Size - will get injected automatically server side
	 * @noUI
	 */
	size?: string;
}

export interface All {
	/**
	 * All Items Link - will get injected automatically server side
	 *
	 * @noUI
	 */
	link?: IImage;
	/**
	 * All Items Label
	 */
	label?: string;
}

export interface IInvestorKitProps extends IBaseElementProps {
	/**
	 * Investor Kit Heading
	 */
	heading?: string;
	/**
	 * Investor Kit Items
	 */
	items?: IInvestorKitItems[];
	/**
	 * Investor Kit All Items.  Please note, we will automatically generate the download URL on the front end based on the
	 * files you have added above.
	 */
	all?: All;
}

/**
 * Investor Kit
 * @block
 * @icon download
 */
const InvestorKit: React.FC<IInvestorKitProps> = ({
	heading,
	items = [],
	all,
	id,
	className,
	classNames = [],
}) => {
	// Using this trick to prevent a flash of sizes saved to the DB.  We adjust sizes for the front end, so they'll be
	// different potentially ( extra decimal place, translated to french, etc ), and I don't like the flash of different data.
	const [ showSize, setShowSize ] = useState(false);
	useEffect( () => {
		setShowSize(true);
	}, [] );

	return (
	<div id={id} className={className || cn('calian-investor-kit', ...classNames)}>
		<h4 className="calian-investor-kit__heading">{heading}</h4>
		<ul className="calian-investor-kit__items">
			{items.length > 0 &&
				items.map(
					({ link, size, label }, index) =>
						link && (
							<li
								className="calian-investor-kit__item"
								key={index}
							>
								<a
									href={link.src}
									target="_blank"
									className="calian-investor-kit__link"
									rel="noreferrer noopener"
								>
									{label && (
										<span className="calian-investor-kit__label">
											{label}
										</span>
									)}
									{( link.prettySize || size ) && (
										<span className="calian-investor-kit__size">
											{showSize && <>({link.prettySize || size})</>}
											<span className="calian-investor-kit__icon icon icon-download"></span>
										</span>
									)}
								</a>
							</li>
						),
				)}
		</ul>
		{all?.label && (
			<a
				href={all?.link?.src || '#generated-automatically-for-front-end'}
				target="_blank"
				className="calian-investor-kit__all btn btn-sm btn-primary"
				rel="noreferrer noopener"
			>
				{all?.label || 'Download all'}
			</a>
		)}
	</div>
)};

export default InvestorKit;
